import { useCallback, useEffect, useState } from 'react';
import {
  fetchCongresses, lookupCongress, saveCongress, fetchAbstracts, addAbstract, updateAbstract,
  fetchReports, saveReport,
  OUTCOMES, type Congress, type Abstract, type Lookup, type Report,
} from '../lib/congress';
import { uploadFile, fetchAttachments, signedUrl, type Attachment } from '../lib/uploads';
import { onFail } from '../lib/errors';
import './congress.css';

/* =====================================================================================
   Congress: the conference calendar and what we put into it.

   Adding one is typed, not typed-in: name the congress and the researcher fetches dates,
   venue, submission site and deadlines from the live web, with sources, for a person to
   check before saving. The abstract deadline then nags through the morning sweep until it
   passes. Each congress carries its abstracts: the file, the submission reference, and the
   outcome, from drafting through poster, mini oral, oral, or the rejection that stings.

   It also carries what the congress produced: the conference report. Those are listed on
   this screen under their own heading and again inside each congress, because a debrief is
   looked for both ways: by remembering the meeting, and by not remembering it.
   ===================================================================================== */

const fmtD = (s: string | null | undefined) => (s ? new Date(s + 'T00:00:00')
  .toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const daysTo = (s: string | null) => (s ? Math.ceil((new Date(s + 'T00:00:00').getTime() - Date.now()) / 864e5) : null);
const OUTCOME_PILL: Record<string, string> = {
  drafting: 'mute', submitted: 'info', under_review: 'info',
  accepted_poster: 'ok', accepted_mini_oral: 'ok', accepted_oral: 'ok',
  rejected: 'warn', withdrawn: 'mute',
};
const outcomeLabel = (k: string) => OUTCOMES.find(([key]) => key === k)?.[1] ?? k;
const isHttp = (v: string | null | undefined): v is string => !!v && /^https?:\/\//i.test(v);

export function CongressView() {
  const [rows, setRows] = useState<Congress[]>([]);
  const [abstracts, setAbstracts] = useState<Abstract[]>([]);
  const [reports, setReports] = useState<Report[]>([]);
  const [adding, setAdding] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [openReportId, setOpenReportId] = useState<string | null>(null);
  /* Writing a report needs a congress to hang it off. Started from a congress we know which
     one; started from the reports heading the writer picks one, so this holds either. */
  const [writingFor, setWritingFor] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(() => {
    fetchCongresses().then(setRows).catch(onFail('The congress calendar', () => setRows([])));
    fetchAbstracts().then(setAbstracts).catch(() => setAbstracts([]));
    fetchReports().then(setReports).catch(() => setReports([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = rows.find((c) => c.id === openId) ?? null;
  const openReport = reports.find((r) => r.id === openReportId) ?? null;
  const congressName = (id: string) => {
    const c = rows.find((x) => x.id === id);
    return c ? (c.short_name || c.name) : 'a congress';
  };

  return (
    <div className="page congress">
      <div className="page-head">
        <h1 className="page-title">Congress</h1>
        <p className="page-sub">
          The conferences we attend, their deadlines on the morning sweep, every abstract with
          its outcome, and the report written afterwards. Name a congress and the details are
          researched from the live web, with sources, for you to check before saving.
        </p>
      </div>

      {msg && <p className="block-note" role="status">{msg}</p>}

      <div className="toolbar">
        <button className="btn primary" onClick={() => setAdding(true)}>Add a congress</button>
      </div>

      <section className="cg-grid">
        {rows.length === 0 && (
          <p className="module-empty">
            Nothing in the calendar. Add the next congress by name and let the researcher do
            the form-filling.
          </p>
        )}
        {rows.map((c) => {
          const d = daysTo(c.abstract_deadline);
          const mine = abstracts.filter((a) => a.congress_id === c.id);
          const mineReports = reports.filter((r) => r.congress_id === c.id);
          return (
            <article key={c.id} className="card cg-card" onClick={() => setOpenId(c.id)}>
              <div className="cg-card-head">
                <strong>{c.short_name || c.name}</strong>
                {c.priority && <span className="pill mute">{c.priority.replace('_', ' ')}</span>}
              </div>
              <div className="cg-when">
                {fmtD(c.starts_on)}{c.ends_on && c.ends_on !== c.starts_on && <> to {fmtD(c.ends_on)}</>}
                {c.city && <> · {c.city}</>}{c.venue && <> · {c.venue}</>}
              </div>
              {c.abstract_deadline && (
                <div className={`cg-deadline ${d != null && d < 0 ? 'past' : d != null && d <= 14 ? 'soon' : ''}`}>
                  Abstracts {d != null && d < 0 ? `closed ${fmtD(c.abstract_deadline)}`
                    : `close ${fmtD(c.abstract_deadline)}${d != null ? ` (${d} day${d === 1 ? '' : 's'})` : ''}`}
                </div>
              )}
              {(isHttp(c.url) || isHttp(c.abstract_url)) && (
                <div className="cg-links">
                  {isHttp(c.url) && (
                    <a href={c.url} target="_blank" rel="noreferrer"
                       onClick={(e) => e.stopPropagation()}>Website ↗</a>
                  )}
                  {isHttp(c.abstract_url) && (
                    <a href={c.abstract_url} target="_blank" rel="noreferrer"
                       onClick={(e) => e.stopPropagation()}>Submit an abstract ↗</a>
                  )}
                </div>
              )}
              {(mine.length > 0 || mineReports.length > 0) && (
                <div className="cg-abs-summary">
                  {mine.map((a) => (
                    <span key={a.id} className={`pill ${OUTCOME_PILL[a.outcome] ?? 'mute'}`}>
                      {outcomeLabel(a.outcome)}
                    </span>
                  ))}
                  {mineReports.length > 0 && (
                    <span className="pill info">
                      {mineReports.length} report{mineReports.length === 1 ? '' : 's'}
                    </span>
                  )}
                </div>
              )}
            </article>
          );
        })}
      </section>

      <section className="cg-reports">
        <div className="cg-abs-head">
          <h2>Conference reports</h2>
          <button className="btn" disabled={rows.length === 0}
                  onClick={() => setWritingFor(openId ?? rows[0]?.id ?? null)}>
            Write a report
          </button>
        </div>
        {rows.length === 0 ? (
          <p className="module-empty">
            A report belongs to a congress. Add the congress first and the report has
            somewhere to live.
          </p>
        ) : reports.length === 0 ? (
          <p className="module-empty">
            No reports yet. The debrief written after a meeting belongs here, where the next
            person going to that congress will look for it.
          </p>
        ) : (
          reports.map((r) => (
            <article key={r.id} className="cg-rep-row" onClick={() => setOpenReportId(r.id)}>
              <div className="cg-abs-main">
                <strong>{r.title}</strong>
                <div className="cell-sub">
                  {congressName(r.congress_id)} · {fmtD(r.report_date)}
                  {r.attended_by && <> · {r.attended_by}</>}
                </div>
                {r.summary && <p className="cg-rep-sum">{r.summary}</p>}
              </div>
              <span className={`pill ${r.status === 'final' ? 'ok' : 'mute'}`}>
                {r.status === 'final' ? 'Final' : 'Draft'}
              </span>
            </article>
          ))
        )}
      </section>

      {adding && (
        <AddCongress onClose={() => setAdding(false)}
                     onDone={(name) => { setAdding(false); setMsg(`${name} added to the calendar.`); load(); }} />
      )}
      {open && (
        <CongressDrawer c={open} abstracts={abstracts.filter((a) => a.congress_id === open.id)}
                        reports={reports.filter((r) => r.congress_id === open.id)}
                        onOpenReport={setOpenReportId}
                        onWriteReport={() => setWritingFor(open.id)}
                        onClose={() => setOpenId(null)} onChanged={load} />
      )}
      {openReport && (
        <ReportDrawer r={openReport} congress={congressName(openReport.congress_id)}
                      onClose={() => setOpenReportId(null)} onChanged={load} />
      )}
      {writingFor && (
        <WriteReport congresses={rows} congressId={writingFor}
                     onClose={() => setWritingFor(null)}
                     onDone={(t) => { setWritingFor(null); setMsg(`${t} filed.`); load(); }} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ add via research */

function lookupToRow(r: Lookup): Partial<Congress> {
  return {
    name: r.name, short_name: r.short_name, edition: r.edition,
    starts_on: r.starts_on, ends_on: r.ends_on, city: r.city,
    country: r.country_iso3, venue: r.venue, url: r.url, abstract_url: r.abstract_url,
    abstract_deadline: r.abstract_deadline, early_reg_deadline: r.early_reg_deadline,
    expected_attendance: r.expected_attendance,
    notes: r.confidence_notes ? `Researched ${new Date().toISOString().slice(0, 10)}: ${r.confidence_notes}` : null,
  };
}

function AddCongress({ onClose, onDone }: { onClose: () => void; onDone: (name: string) => void }) {
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [found, setFound] = useState<Lookup | null>(null);
  const [draft, setDraft] = useState<Partial<Congress>>({});

  async function research() {
    setBusy(true); setErr(null);
    try {
      const r = await lookupCongress(q.trim());
      setFound(r); setDraft(lookupToRow(r));
    } catch (e) { setErr(String((e as Error).message)); }
    finally { setBusy(false); }
  }

  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <div className="drawer cg-drawer">
        <h2>Add a congress</h2>
        {err && <p className="block-note">{err}</p>}
        {!found && (
          <>
            <label className="cf-field"><span>Which one</span>
              <input className="input" value={q} autoFocus placeholder="ERA Congress 2027, ASN Kidney Week, ATC..."
                     onChange={(e) => setQ(e.target.value)}
                     onKeyDown={(e) => { if (e.key === 'Enter' && q.trim()) void research(); }} /></label>
            <div className="exp-decide-actions">
              <button className="btn primary" disabled={busy || !q.trim()} onClick={research}>
                {busy ? 'Researching the live web...' : 'Look it up'}
              </button>
              <button className="btn" onClick={onClose}>Cancel</button>
            </div>
            <p className="muted cg-note">
              Dates, venue, submission site and deadlines come back with sources. You check
              them before anything is saved.
            </p>
          </>
        )}
        {found && (
          <>
            <CongressFields draft={draft} setDraft={setDraft} />
            {found.confidence_notes && <p className="cg-conf">{found.confidence_notes}</p>}
            {found.sources?.length > 0 && (
              <div className="cg-sources">
                {found.sources.map((s, i) => (
                  <a key={i} href={s.url} target="_blank" rel="noreferrer">{s.title || s.url}</a>
                ))}
              </div>
            )}
            <div className="exp-decide-actions">
              <button className="btn primary" disabled={busy || !draft.name} onClick={async () => {
                setBusy(true);
                try { await saveCongress(draft); onDone(draft.short_name || draft.name || 'Congress'); }
                catch (e) { setErr(String((e as Error).message)); setBusy(false); }
              }}>Save to the calendar</button>
              <button className="btn" onClick={() => { setFound(null); }}>Search again</button>
              <button className="btn" onClick={onClose}>Cancel</button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function CongressFields({ draft, setDraft }: {
  draft: Partial<Congress>; setDraft: (d: Partial<Congress>) => void;
}) {
  const f = (k: keyof Congress, label: string, type = 'text', ph = '') => (
    <label className="cf-field"><span>{label}
      {type === 'url' && isHttp(draft[k] as string | null) && (
        <a className="cf-open" href={draft[k] as string} target="_blank" rel="noreferrer">open ↗</a>
      )}</span>
      <input className="input" type={type === 'url' ? 'text' : type} placeholder={ph}
             value={(draft[k] as string | number | null) ?? ''}
             onChange={(e) => setDraft({ ...draft, [k]: e.target.value || null })} /></label>
  );
  return (
    <>
      {f('name', 'Name')}
      <div className="cg-two">{f('short_name', 'Short name')}{f('edition', 'Edition')}</div>
      <div className="cg-two">{f('starts_on', 'Starts', 'date')}{f('ends_on', 'Ends', 'date')}</div>
      <div className="cg-two">{f('city', 'City')}{f('venue', 'Venue')}</div>
      <div className="cg-two">
        {f('abstract_deadline', 'Abstract deadline', 'date')}
        {f('early_reg_deadline', 'Early registration ends', 'date')}
      </div>
      {f('url', 'Website', 'url')}
      {f('abstract_url', 'Abstract submission site', 'url')}
    </>
  );
}

/* ------------------------------------------------------------------ detail */

function CongressDrawer({ c, abstracts, reports, onOpenReport, onWriteReport, onClose, onChanged }: {
  c: Congress; abstracts: Abstract[]; reports: Report[];
  onOpenReport: (id: string) => void; onWriteReport: () => void;
  onClose: () => void; onChanged: () => void;
}) {
  const [draft, setDraft] = useState<Partial<Congress>>({ ...c });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [addingAbs, setAddingAbs] = useState(false);

  useEffect(() => { setDraft({ ...c }); }, [c]);

  async function refresh() {
    setRefreshing(true); setErr(null);
    try {
      const r = await lookupCongress(`${c.name} ${c.edition ?? ''}`.trim(), c);
      const next = lookupToRow(r);
      const changed = Object.entries(next).filter(([k, v]) =>
        k !== 'notes' && v != null && String(v) !== String((c as never as Record<string, unknown>)[k] ?? ''));
      setDraft({ ...draft, ...Object.fromEntries(changed), notes: next.notes ?? draft.notes });
      setErr(changed.length
        ? `The researcher proposes ${changed.length} change(s): ${changed.map(([k]) => k.replace(/_/g, ' ')).join(', ')}. Review the fields and Save.`
        : 'Nothing has changed since this was last researched.');
    } catch (e) { setErr(String((e as Error).message)); }
    finally { setRefreshing(false); }
  }

  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <div className="drawer cg-drawer">
        <h2>{c.short_name || c.name}</h2>
        {err && <p className="block-note">{err}</p>}
        <CongressFields draft={draft} setDraft={setDraft} />
        <div className="exp-decide-actions">
          <button className="btn primary" disabled={busy} onClick={async () => {
            setBusy(true);
            try { await saveCongress({ ...draft, id: c.id }); setErr(null); onChanged(); }
            catch (e) { setErr(String((e as Error).message)); }
            finally { setBusy(false); }
          }}>Save</button>
          <button className="btn" disabled={refreshing} onClick={refresh}>
            {refreshing ? 'Checking the live web...' : 'Refresh from the web'}
          </button>
          <button className="btn" onClick={onClose}>Done</button>
        </div>

        <section className="cg-abs">
          <div className="cg-abs-head">
            <h3>Abstracts</h3>
            <button className="btn" onClick={() => setAddingAbs(true)}>Add an abstract</button>
          </div>
          {abstracts.length === 0 && <p className="muted">None yet for this congress.</p>}
          {abstracts.map((a) => (
            <AbstractRow key={a.id} a={a} onChanged={onChanged} onErr={setErr} />
          ))}
        </section>

        {addingAbs && (
          <AddAbstract congressId={c.id}
                       onClose={() => setAddingAbs(false)}
                       onDone={() => { setAddingAbs(false); onChanged(); }} onErr={setErr} />
        )}

        <section className="cg-abs">
          <div className="cg-abs-head">
            <h3>Conference reports</h3>
            <button className="btn" onClick={onWriteReport}>Write a report</button>
          </div>
          {reports.length === 0 && <p className="muted">No report filed for this one yet.</p>}
          {reports.map((r) => (
            <button key={r.id} className="cg-rep-link" onClick={() => onOpenReport(r.id)}>
              <span>
                <strong>{r.title}</strong>
                <span className="cell-sub">
                  {fmtD(r.report_date)}{r.attended_by && <> · {r.attended_by}</>}
                </span>
              </span>
              <span className={`pill ${r.status === 'final' ? 'ok' : 'mute'}`}>
                {r.status === 'final' ? 'Final' : 'Draft'}
              </span>
            </button>
          ))}
        </section>
      </div>
    </>
  );
}

function AbstractRow({ a, onChanged, onErr }: {
  a: Abstract; onChanged: () => void; onErr: (s: string | null) => void;
}) {
  const [files, setFiles] = useState<(Attachment & { href: string | null })[]>([]);
  useEffect(() => {
    // Sign the download links up front so each file renders as a real hyperlink: a
    // click-then-sign button loses the user gesture and gets eaten by popup blockers,
    // which on the iPad looked like the file simply refusing to open.
    fetchAttachments('congress_abstract', a.id)
      .then((atts) => Promise.all(atts.map(async (f) => ({
        ...f,
        href: await signedUrl(f.bucket as never, f.path, 3600).catch(() => null),
      }))))
      .then(setFiles).catch(() => setFiles([]));
  }, [a.id]);

  return (
    <div className="cg-abs-row">
      <div className="cg-abs-main">
        <strong>{a.title}</strong>
        {a.authors && <div className="cell-sub">{a.authors}</div>}
        <div className="cell-sub">
          {a.abstract_ref && <>ref {a.abstract_ref} · </>}
          {a.submitted_on && <>submitted {fmtD(a.submitted_on)} · </>}
          {files.map((f) => (f.href ? (
            <a key={f.id} className="cg-filelink" href={f.href} target="_blank"
               rel="noreferrer">{f.filename} ↗</a>
          ) : (
            <button key={f.id} className="link-btn" onClick={async () => {
              try {
                const u = await signedUrl(f.bucket as never, f.path, 3600);
                const l = document.createElement('a');
                l.href = u; l.target = '_blank'; l.rel = 'noreferrer';
                document.body.appendChild(l); l.click(); l.remove();
              } catch (e) { onErr(String((e as Error).message)); }
            }}>{f.filename}</button>
          )))}
        </div>
      </div>
      <select className={`input sel cg-outcome o-${a.outcome}`} value={a.outcome}
              onChange={(e) => updateAbstract(a.id, {
                outcome: e.target.value,
                decision_on: e.target.value.startsWith('accepted') || e.target.value === 'rejected'
                  ? new Date().toISOString().slice(0, 10) : a.decision_on,
              }).then(onChanged).catch((er) => onErr(String((er as Error).message)))}>
        {OUTCOMES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
    </div>
  );
}

function AddAbstract({ congressId, onClose, onDone, onErr }: {
  congressId: string; onClose: () => void; onDone: () => void; onErr: (s: string | null) => void;
}) {
  const [f, setF] = useState({ title: '', authors: '', presenter: '', abstractRef: '',
    submittedOn: '' });
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: string) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  return (
    <div className="cg-addabs">
      <h3>Add an abstract</h3>
      <label className="cf-field"><span>Title</span>
        <input className="input" value={f.title} onChange={set('title')} /></label>
      <label className="cf-field"><span>Authors, as on the abstract</span>
        <input className="input" value={f.authors} onChange={set('authors')} /></label>
      <div className="cg-two">
        <label className="cf-field"><span>Presenter</span>
          <input className="input" value={f.presenter} onChange={set('presenter')} /></label>
        <label className="cf-field"><span>Submission reference</span>
          <input className="input" value={f.abstractRef} onChange={set('abstractRef')} /></label>
      </div>
      <div className="cg-two">
        <label className="cf-field"><span>Submitted on (blank if still drafting)</span>
          <input className="input" type="date" value={f.submittedOn} onChange={set('submittedOn')} /></label>
        <label className="cf-field"><span>The abstract file</span>
          <label className="btn cg-file">{file ? file.name : 'Choose a file'}
            <input type="file" hidden onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </label></label>
      </div>
      <div className="exp-decide-actions">
        <button className="btn primary" disabled={busy || !f.title.trim()} onClick={async () => {
          setBusy(true); onErr(null);
          try {
            const id = await addAbstract({ congressId, ...f });
            if (file) {
              await uploadFile({ file, bucket: 'general', objectType: 'congress_abstract',
                objectId: id, role: 'submitted', description: f.title });
            }
            onDone();
          } catch (e) { onErr(String((e as Error).message)); setBusy(false); }
        }}>Add it</button>
        <button className="btn" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ conference reports */

/* A debrief is not a paragraph, and a <pre> block makes a sixteen-page one unreadable. The
   body is plain text with two conventions people already use when they paste: '## ' starts
   a heading, '- ' starts a bullet. Everything else is a paragraph. No Markdown library,
   no HTML from text we did not write. */
function ReportBody({ text }: { text: string }) {
  const lines = (text || '').replace(/\r\n/g, '\n').split('\n');
  const out: JSX.Element[] = [];
  let para: string[] = [];
  let bullets: string[] = [];
  const flushPara = () => {
    if (para.length) { out.push(<p key={`p${out.length}`}>{para.join(' ')}</p>); para = []; }
  };
  const flushBullets = () => {
    if (bullets.length) {
      out.push(<ul key={`u${out.length}`}>{bullets.map((b, i) => <li key={i}>{b}</li>)}</ul>);
      bullets = [];
    }
  };
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (/^#{1,6}\s/.test(line)) {
      flushPara(); flushBullets();
      out.push(<h3 key={`h${out.length}`}>{line.replace(/^#{1,6}\s+/, '')}</h3>);
    } else if (/^[-*]\s+/.test(line)) {
      flushPara();
      bullets.push(line.replace(/^[-*]\s+/, ''));
    } else if (!line.trim()) {
      flushPara(); flushBullets();
    } else {
      flushBullets();
      para.push(line.trim());
    }
  }
  flushPara(); flushBullets();
  return <div className="cg-rep-body">{out}</div>;
}

function ReportDrawer({ r, congress, onClose, onChanged }: {
  r: Report; congress: string; onClose: () => void; onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Partial<Report>>({ ...r });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [files, setFiles] = useState<(Attachment & { href: string | null })[]>([]);

  useEffect(() => { setDraft({ ...r }); setEditing(false); }, [r]);
  useEffect(() => {
    fetchAttachments('congress_report', r.id)
      .then((atts) => Promise.all(atts.map(async (f) => ({
        ...f, href: await signedUrl(f.bucket as never, f.path, 3600).catch(() => null),
      }))))
      .then(setFiles).catch(() => setFiles([]));
  }, [r.id]);

  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <div className="drawer cg-drawer cg-rep-drawer">
        <h2>{r.title}</h2>
        <p className="cg-rep-meta">
          {congress} · {fmtD(r.report_date)}
          {r.attended_by && <> · {r.attended_by}</>}
          {' · '}{r.status === 'final' ? 'Final' : 'Draft'}
        </p>
        {err && <p className="block-note">{err}</p>}
        {files.length > 0 && (
          <div className="cg-links">
            {files.map((f) => (f.href
              ? <a key={f.id} href={f.href} target="_blank" rel="noreferrer">{f.filename} ↗</a>
              : <span key={f.id} className="muted">{f.filename}</span>))}
          </div>
        )}

        {!editing ? (
          <>
            {r.summary && <p className="cg-conf">{r.summary}</p>}
            <ReportBody text={r.body} />
            <div className="exp-decide-actions">
              <button className="btn" onClick={() => setEditing(true)}>Edit</button>
              <button className="btn" onClick={onClose}>Done</button>
            </div>
          </>
        ) : (
          <>
            <ReportFields draft={draft} setDraft={setDraft} />
            <div className="exp-decide-actions">
              <button className="btn primary" disabled={busy || !draft.title?.trim()} onClick={async () => {
                setBusy(true); setErr(null);
                try { await saveReport({ ...draft, id: r.id }); setEditing(false); onChanged(); }
                catch (e) { setErr(String((e as Error).message)); }
                finally { setBusy(false); }
              }}>Save</button>
              <button className="btn" onClick={() => { setDraft({ ...r }); setEditing(false); }}>Cancel</button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function ReportFields({ draft, setDraft }: {
  draft: Partial<Report>; setDraft: (d: Partial<Report>) => void;
}) {
  return (
    <>
      <label className="cf-field"><span>Title</span>
        <input className="input" value={draft.title ?? ''}
               onChange={(e) => setDraft({ ...draft, title: e.target.value })} /></label>
      <div className="cg-two">
        <label className="cf-field"><span>Who attended</span>
          <input className="input" value={draft.attended_by ?? ''}
                 onChange={(e) => setDraft({ ...draft, attended_by: e.target.value || null })} /></label>
        <label className="cf-field"><span>Date of the report</span>
          <input className="input" type="date" value={draft.report_date ?? ''}
                 onChange={(e) => setDraft({ ...draft, report_date: e.target.value })} /></label>
      </div>
      <label className="cf-field"><span>In one paragraph</span>
        <textarea className="input cg-rep-sumbox" rows={3} value={draft.summary ?? ''}
                  onChange={(e) => setDraft({ ...draft, summary: e.target.value || null })} /></label>
      <label className="cf-field">
        <span>The report. Start a line with ## for a heading and - for a bullet.</span>
        <textarea className="input cg-rep-bodybox" rows={18} value={draft.body ?? ''}
                  onChange={(e) => setDraft({ ...draft, body: e.target.value })} /></label>
      <label className="cf-field"><span>Status</span>
        <select className="input sel" value={draft.status ?? 'draft'}
                onChange={(e) => setDraft({ ...draft, status: e.target.value })}>
          <option value="draft">Draft</option>
          <option value="final">Final</option>
        </select></label>
    </>
  );
}

function WriteReport({ congresses, congressId, onClose, onDone }: {
  congresses: Congress[]; congressId: string; onClose: () => void; onDone: (title: string) => void;
}) {
  const [cid, setCid] = useState(congressId);
  const [draft, setDraft] = useState<Partial<Report>>({
    title: '', attended_by: '', report_date: new Date().toISOString().slice(0, 10),
    summary: '', body: '', status: 'draft',
  });
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <div className="drawer cg-drawer cg-rep-drawer">
        <h2>Write a conference report</h2>
        {err && <p className="block-note">{err}</p>}
        <label className="cf-field"><span>Which congress</span>
          <select className="input sel" value={cid} onChange={(e) => setCid(e.target.value)}>
            {congresses.map((c) => (
              <option key={c.id} value={c.id}>{c.short_name || c.name}</option>
            ))}
          </select></label>
        <ReportFields draft={draft} setDraft={setDraft} />
        <label className="cf-field"><span>The source document, if there is one</span>
          <label className="btn cg-file">{file ? file.name : 'Choose a file'}
            <input type="file" hidden onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </label></label>
        <div className="exp-decide-actions">
          <button className="btn primary" disabled={busy || !draft.title?.trim() || !cid}
                  onClick={async () => {
            setBusy(true); setErr(null);
            try {
              const id = await saveReport({ ...draft, congress_id: cid });
              if (file) {
                await uploadFile({ file, bucket: 'general', objectType: 'congress_report',
                  objectId: id, role: 'attachment', description: draft.title || 'Conference report' });
              }
              onDone(draft.title || 'The report');
            } catch (e) { setErr(String((e as Error).message)); setBusy(false); }
          }}>File it</button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </>
  );
}
